import makeInspectable from 'mobx-devtools-mst'
import { Instance, types } from 'mobx-state-tree'
import {
  BankAccount,
  editableModel,
  IBankAccountModel,
  IInvoiceModel,
  routes,
  routes2,
} from '@linqpal/models'
import {
  INITIAL,
  PAY_STATUS,
  PAYMENT_METHODS,
} from '@linqpal/models/src/dictionaries'
import numbro from 'numbro'
import { paths } from '../../../../../links'

const props = {
  initial: types.optional(types.string, 'addPayment'),
  current: types.optional(types.string, 'addPayment'),
  cardFlowCurrent: types.optional(types.string, INITIAL),
  addModal: types.optional(types.boolean, false),
  collectPayment: types.optional(types.boolean, false),
  paymentStatus: types.maybeNull(types.enumeration(Object.values(PAY_STATUS))),
  invoice: types.maybeNull(types.frozen<IInvoiceModel>()),
  loading: types.optional(types.boolean, false),
  paymentMethodToRemove: types.maybeNull(
    types.frozen<{ id: string; name: string; type: 'bank' | 'card' }>(),
  ),
}

function views(self) {
  return {}
}

function actions(_self) {
  const self = _self as ICustomerPaymentMethodModel
  return {
    setInitial: (i) => {
      self.initial = i
    },
    setCurrent: (c) => {
      self.current = c
    },
    setCardFlowCurrent: (c) => {
      self.cardFlowCurrent = c
    },
    setAddModal: (val) => {
      self.addModal = val
    },
    setCollectPayment: (val) => {
      self.collectPayment = val
    },
    setLoading: (val) => {
      self.loading = val
    },
    setPaymentMethod: (data) => {
      self.paymentMethod = data
    },
    setPaymentStatus: (status) => {
      self.paymentStatus = status
    },
    formatCurrency: (value) => {
      return numbro(value).formatCurrency({
        thousandSeparated: true,
        mantissa: 2,
      })
    },
    onCollectPayment: (inv, navigation, setDraftModel) => {
      self.setCollectPayment(true)

      if ((inv.customer.bankAccounts?.length as number) > 1) {
        self.setCurrent('choosePaymentMethod')
        self.setAddModal(true)
        self.setPaymentMethod(null)
      } else if (!inv.customer.bankAccounts?.length) {
        setDraftModel(inv.customer)
        navigation.navigate(paths.Console.customers._self, {
          screen: paths.Console.customers.home.TradeCredit,
        })
        self.setAddModal(true)
        self.setCurrent('addPayment')
        self.setPaymentMethod(null)
        self.setInvoice(inv)
      } else {
        self.setCurrent('payInvoice')
        self.setAddModal(true)
        self.setPaymentMethod(inv.customer.bankAccounts[0])
      }
    },
    getPricingPackage: (invoice, setPackage) => {
      self.setLoading(true)
      routes2.invoice
        .getPricingPackageFee({
          invoiceIds: [invoice.id],
          accountId: (
            self.paymentMethod as unknown as IBankAccountModel
          )?._id.toString(),
        })
        .then((resp) => {
          if (resp.pricingPackage) {
            setPackage({
              ...resp.pricingPackage,
              totalAmount: self.formatCurrency(resp.pricingPackage.totalAmount),
              fee: self.formatCurrency(resp.pricingPackage.fee),
            })
          }
          self.setLoading(false)
        })
    },
    collect: (invoiceId) => {
      self.setCurrent('paymentProcessing')
      const paymentMethod =
        (self.paymentMethod as unknown as IBankAccountModel)
          .paymentMethodType === 'bank'
          ? PAYMENT_METHODS.ACH
          : PAYMENT_METHODS.CARD
      routes.invoices
        .collect({
          invoiceId,
          paymentMethod,
          accountId: (self.paymentMethod as unknown as IBankAccountModel)._id,
        })
        .then(() => {
          self.setPaymentStatus(PAY_STATUS.SUCCESS)
        })
        .catch(() => {
          self.setPaymentStatus(PAY_STATUS.ERROR)
        })
        .finally(() => {
          self.setCurrent('paymentStatus')
        })
    },
    setInvoice: (data) => {
      self.invoice = data
    },
    setPaymentMethodToRemove: (data: {
      id: string
      name: string
      paymentMethodType: 'bank' | 'card'
    }) => {
      self.paymentMethodToRemove = {
        id: data.id,
        name: data.name,
        type: data.paymentMethodType,
      }
      self.setCurrent('paymentMethodRemove')
    },
  }
}

export const CustomerPaymentMethodModel = types
  .compose(
    types
      .model(props)
      .volatile(() => ({
        paymentMethod: types.maybeNull(BankAccount),
      }))
      .views(views)
      .actions(actions),
    editableModel(),
  )
  .named('CustomerPaymentMethodStore')

const store = CustomerPaymentMethodModel.create({})
makeInspectable(store)
export default store

interface ICustomerPaymentMethodModel
  extends Instance<typeof CustomerPaymentMethodModel> {}
